import { faThumbsUp, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { ActionButton, ReturnStickerSelection } from 'components';
import { HeadingItem } from 'components/heading-item/heading-item';
import { TripContext } from 'contexts';
import { DateBox, NumberBox, Popup, ScrollView, SelectBox, TextBox } from 'devextreme-react';
import { SecondaryPopUpField } from 'enums';
import { isUndefined } from 'lodash';
import React, { FC, Fragment, MutableRefObject, useContext, useRef } from 'react';
import { ReconcileReasonType } from 'services/enums';
import { ReconcileReason, RefusalReason } from 'services/types';
import { SecondaryPopUpValue } from 'types';
import './secondary-pop-up.scss';

type Props = {
    isVisible: boolean,
    isReturnStickerAvailable: boolean,
    heading: string,
    reconcileReasons?: ReconcileReason[],
    refusalReasons: RefusalReason[],
    secondaryPopUpValue: SecondaryPopUpValue,
    onClickOk: (values: SecondaryPopUpValue) => void,
    onClickCancel: () => void,
    onValueChanged: (secondaryPopUpField: SecondaryPopUpField, value: any) => void,
}

export const SecondaryPopup: FC<Props> = ({ isVisible, isReturnStickerAvailable, heading, secondaryPopUpValue, refusalReasons, onClickOk, onValueChanged, onClickCancel, reconcileReasons = [] }) => {

    const controls = {
        weight: useRef<any>(),
        reconcileReason: useRef<any>(),
        refusalReason: useRef<any>(),
        codeDate: useRef<any>(),
        returnSticker: useRef<any>(),
        noReturnStickerReason: useRef<any>(),
        comments: useRef<any>()
    }

    const handleDropDownValueChanged = (field: SecondaryPopUpField, value: any, nextControl?: MutableRefObject<any>) => {
        onValueChanged(field, value);
        nextControl?.current?.instance?.focus()
    }

    const tripContext = useContext(TripContext)

    const selectedRefusal: RefusalReason = refusalReasons.find(reason => reason.id === secondaryPopUpValue.refusalChoiceId) ?? {} as RefusalReason
    const selectedReconcile: ReconcileReason = tripContext.reconcileReasons.find(reason => reason.id === secondaryPopUpValue.reconcileReasonId) ?? {} as ReconcileReason
    const refusalfilter: RefusalReason[] = refusalReasons.filter(reason => reason.reconcileReasonId == secondaryPopUpValue.reconcileReasonId)
    const backgroundStyle = { backgroundColor: refusalfilter.length == 0 ? '#ebebeb' : 'transparent' }

    const requiresDate: boolean = secondaryPopUpValue.reconcileReasonId === ReconcileReasonType.ShelfLife
    const activeReturnStickerControl = isReturnStickerAvailable ? controls.returnSticker : controls.noReturnStickerReason

    return (
        <Popup
            visible={isVisible}
            dragEnabled={false}
            closeOnOutsideClick={false}
            showTitle={false}
            width={'90vw'}
            height={'85vh'}
        >
            <ScrollView>
                <div className="secondary-popup-card" >
                    <div style={{ paddingBottom: '3em' }}>
                        <HeadingItem text={heading}></HeadingItem>
                        {reconcileReasons.length > 0 &&
                            <>
                                {reconcileReasons.length == 2 &&
                                    <div className={'secondary-popup-field'}>
                                        <NumberBox
                                            value={secondaryPopUpValue.addCatchWeight!}
                                            placeholder="Weight"
                                            onValueChanged={e => onValueChanged(SecondaryPopUpField.AddCatchWeight, e.value)}
                                            ref={(control) => controls.weight.current = control}
                                        />
                                    </div>
                                }
                                <div className={'secondary-popup-field'}>
                                    <SelectBox
                                        items={reconcileReasons}
                                        displayExpr={'description'}
                                        defaultValue={null}
                                        value={selectedReconcile}
                                        itemRender={(itemData: ReconcileReason) => <div className={'reconcile-selectionbox'}>{itemData.description}</div>}
                                        onValueChanged={e => !isUndefined(e.value.id) && selectedReconcile?.id != e.value.id && onValueChanged(SecondaryPopUpField.ReconcileReasonId, e.value.id)}
                                        ref={(control) => controls.reconcileReason.current = control}
                                    >
                                    </SelectBox>
                                </div>
                            </>
                        }
                        <div className={'secondary-popup-field'} style={backgroundStyle}>
                            <SelectBox
                                disabled={refusalfilter.length == 0}
                                items={refusalfilter}
                                displayExpr={'description'}
                                itemRender={(itemData: RefusalReason) => <div className={'reconcile-selectionbox'}>{itemData.description}</div>}
                                value={selectedRefusal}
                                onValueChanged={e => !isUndefined(e.value.id) && selectedRefusal?.id != e.value.id && handleDropDownValueChanged(SecondaryPopUpField.RefusalChoiceId, e.value.id, requiresDate ? undefined : activeReturnStickerControl)}
                                ref={(control) => controls.refusalReason.current = control}
                            >
                            </SelectBox>
                        </div>
                        {requiresDate &&
                            <div className={'secondary-popup-field'}>
                                <DateBox
                                    placeholder="Enter the date (required)"
                                    type="date"
                                    value={secondaryPopUpValue.codeDate!}
                                    onValueChanged={e => handleDropDownValueChanged(SecondaryPopUpField.CodeDate, e.value, activeReturnStickerControl)}
                                    ref={(control) => controls.codeDate.current = control}
                                />
                            </div>
                        }
                        {selectedReconcile.isRefusal &&
                            <div className={'secondary-popup-field'} style={backgroundStyle} >
                                <ReturnStickerSelection
                                    isReturnStickerAvailable={isReturnStickerAvailable}
                                    disabled={refusalfilter.length == 0}
                                    returnSticker={secondaryPopUpValue.returnSticker}
                                    noReturnStickerReason={secondaryPopUpValue.noReturnSticker}
                                    isReturnStickerDuplicate={secondaryPopUpValue.isReturnStickerDuplicate}
                                    onReturnStickerChanged={(value) => onValueChanged(SecondaryPopUpField.ReturnSticker, value)}
                                    onNoReturnStickerReasonChanged={(value) => onValueChanged(SecondaryPopUpField.NoReturnSticker, value)}
                                    returnStickerControlRef={(control) => controls.returnSticker.current = control}
                                    noReturnStickerControlRef={(control) => controls.noReturnStickerReason.current = control}
                                    handleRadioChecked={(value: any) => value == "Yes" ? onValueChanged(SecondaryPopUpField.ReturnSticker, 0) :
                                        onValueChanged(SecondaryPopUpField.NoReturnSticker, "")}
                                />
                            </div>
                        }
                        <div className={'secondary-popup-field'} style={backgroundStyle}>
                            <TextBox
                                placeholder="Please enter any other comments"
                                disabled={refusalfilter.length == 0}
                                value={secondaryPopUpValue.comments!}
                                onValueChanged={e => {
                                    if (e.value === null)
                                        return;
                                    onValueChanged(SecondaryPopUpField.Comments, e.value)
                                }
                                }
                                ref={(textBox) => controls.comments.current = textBox}
                            />
                        </div>
                        <div className={'bottom-fix-action-row'}>
                            <ActionButton
                                icon={faTimes}
                                buttonColor={'#EB5757'}
                                onClick={onClickCancel}
                                buttonText={'Cancel'}
                                iconSize={'1x'}
                            />
                            <ActionButton
                                enabled={secondaryPopUpValue.isReturnStickerDuplicate !== false || (requiresDate ? (requiresDate && secondaryPopUpValue.codeDate != null) : secondaryPopUpValue.enableButton)}
                                icon={faThumbsUp}
                                buttonColor={'#76AB21'}
                                onClick={() => onClickOk(secondaryPopUpValue)}
                                buttonText={'Ok'}
                            />
                        </div>
                    </div>
                </div>
            </ScrollView>
        </Popup>
    )
}